import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Award, CalendarClock, CheckCircle2, ClipboardList, Lock, RotateCcw, SearchX, XCircle } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout, EmptyState, FilePreview, LinkedText,
  PageHeader, ProgressBar, cn,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import CommonToaster from "../../Common/CommonToaster";
import apiClient from "../../../utils/apiClient";
import AssignmentFacts from "./AssignmentFacts";
import StudentSubmissionForm from "./StudentSubmissionForm";
import { formatDue, isDueSoon, percentOf, relativeTime } from "./assignmentRules";

/**
 * The one line at the top that says where the student stands, in the tone of
 * what it means. Everything it reads is the server's `submissionState`.
 */
const StatusBanner = ({ assignment, submission, state }) => {
  const s = state?.state;
  const due = state?.deadline;
  const dueWords = due ? formatDue(due, { dateOnly: state.dateOnly }) : "";

  if (s === "graded") {
    const pct = percentOf(submission.marks, assignment.maxMarks);
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-x-4 tw-gap-y-2">
          <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-md tw-bg-success/10 tw-text-success">
            <Award size={22} aria-hidden="true" />
          </span>
          <div>
            <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">Your grade</p>
            <p className="tw-m-0 tw-text-2xl tw-font-bold tw-tabular-nums tw-text-content">
              {submission.marks}
              <span className="tw-text-base tw-font-medium tw-text-content-subtle"> / {assignment.maxMarks}</span>
              {pct != null && <span className="tw-ml-2 tw-text-base tw-font-semibold tw-text-success">{pct}%</span>}
            </p>
          </div>
        </div>
        {pct != null && <ProgressBar value={pct} color="success" />}
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
          Graded{submission.gradedAt ? ` ${formatDue(submission.gradedAt)}` : ""}.
        </p>
      </div>
    );
  }

  const LINES = {
    resubmit_requested: { Icon: RotateCcw, tone: "warning", title: "Changes requested", body: "Read the note below, change your work and resubmit. The due date doesn't apply to a resubmission." },
    submitted: {
      Icon: CheckCircle2,
      tone: "info",
      title: "Handed in",
      body: `${submission?.submittedAt ? formatDue(submission.submittedAt) : ""}${state?.late ? " — after the due date" : ""}. Waiting to be graded.`,
    },
    missed: { Icon: XCircle, tone: "danger", title: "Missed", body: `It was due ${dueWords}, and this assignment doesn't take late work.` },
    closed: { Icon: Lock, tone: "neutral", title: "Closed", body: "This assignment no longer takes submissions." },
    not_submitted: state?.pastDue
      ? { Icon: CalendarClock, tone: "warning", title: "Past the due date", body: `It was due ${dueWords}. Late work is still accepted, and is marked late.` }
      : {
        Icon: CalendarClock,
        tone: isDueSoon(due) ? "warning" : "primary",
        title: due ? `Due ${relativeTime(due)}` : "Not handed in yet",
        body: due ? `${dueWords}. You haven't handed anything in yet.` : "You haven't handed anything in yet.",
      },
  };
  const line = LINES[s] || LINES.not_submitted;
  const TONE_TILE = {
    primary: "tw-bg-primary/10 tw-text-primary",
    info: "tw-bg-info/10 tw-text-info",
    warning: "tw-bg-warning/10 tw-text-warning",
    danger: "tw-bg-danger/10 tw-text-danger",
    neutral: "tw-bg-surface-sunken tw-text-content-muted",
  };
  return (
    <div className="tw-flex tw-items-start tw-gap-3">
      <span className={cn("tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md", TONE_TILE[line.tone])}>
        <line.Icon size={22} aria-hidden="true" />
      </span>
      <div className="tw-min-w-0">
        <p className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">{line.title}</p>
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">{line.body}</p>
      </div>
    </div>
  );
};

/**
 * StudentAssignmentPage — `/s/batch-assignment/:id`: one assignment from the
 * student's side. Where they stand first, then what to do, then their work.
 *
 * Whether they may hand in, change or resubmit is the server's
 * `submissionState`; the page never decides it from the dates itself.
 */
const StudentAssignmentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ loading: true, error: null, assignment: null, submission: null, submissionState: null });
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const load = useCallback(() => {
    setData((d) => ({ ...d, loading: true, error: null }));
    apiClient.get(`/general-assignments/student/detail/${id}`)
      .then(({ data: res }) => {
        setData({ loading: false, error: null, assignment: res.assignment, submission: res.submission || null, submissionState: res.submissionState || null });
        setEditing(false);
      })
      .catch((error) => setData({ loading: false, error, assignment: null, submission: null, submissionState: null }));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/s/learning"));

  if (data.loading && !data.assignment) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading assignment">
        <PageHeader title="Assignment" icon={ClipboardList} backButton onBack={goBack} />
        <Shimmer height={120} />
        <Shimmer height={220} />
        <Shimmer height={260} />
      </div>
    );
  }

  const { assignment, submission, submissionState: state } = data;
  if (!assignment) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <PageHeader title="Assignment" icon={ClipboardList} backButton onBack={goBack} />
        {data.error?.response?.status === 404 ? (
          <EmptyState
            icon={SearchX}
            title="Assignment not found"
            description="It may have been removed, or it belongs to another batch."
            action={<AppButton variant="outline" onClick={() => navigate("/s/learning")}>Back to my learning</AppButton>}
          />
        ) : (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
            Could not load this assignment.
          </Callout>
        )}
      </div>
    );
  }

  const mode = state?.mode;
  // A first hand-in and a resubmission open straight into the form; work
  // already handed in shows as it is, with a button to change it.
  const showForm = Boolean(state?.canSubmit) && (mode !== "edit" || editing);

  const onSubmitted = (res) => {
    setData((d) => ({ ...d, submission: res.submission, submissionState: res.submissionState }));
    setEditing(false);
    setToast({
      show: true,
      type: "success",
      message: mode === "resubmit" ? "Resubmitted. You'll be notified when it's graded." : "Handed in. You'll be notified when it's graded.",
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader title={assignment.title} description={assignment.batchId?.name || ""} icon={ClipboardList} backButton onBack={goBack} />

      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <StatusBanner assignment={assignment} submission={submission} state={state} />
          <AssignmentFacts
            deadline={state?.deadline || assignment.dueDate}
            dateOnly={state?.dateOnly}
            maxMarks={assignment.maxMarks}
            lateAllowed={state?.lateAllowed}
            closed={assignment.status === "closed"}
            className="tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-pt-3"
          />
        </AppCardContent>
      </AppCard>

      {submission?.feedback && ["graded", "resubmit_requested"].includes(submission.status) && (
        <Callout tone={submission.status === "graded" ? "success" : "warning"}>
          <span className="tw-mb-1 tw-block tw-font-semibold">
            {submission.status === "graded" ? "Feedback" : "What to change"}
          </span>
          <LinkedText text={submission.feedback} className="tw-text-xs" />
        </Callout>
      )}

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Instructions</AppCardTitle>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
          {assignment.description ? (
            <LinkedText text={assignment.description} />
          ) : (
            <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-subtle">No written instructions — see the files below, or ask in class.</p>
          )}
          {assignment.attachments?.length > 0 && (
            <div>
              <p className="tw-mb-1.5 tw-mt-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">Files for this assignment</p>
              <FilePreview attachments={assignment.attachments} />
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>{mode === "resubmit" ? "Resubmit your work" : submission ? "Your work" : "Hand in your work"}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          {showForm ? (
            <StudentSubmissionForm
              key={`${submission?._id || "new"}-${submission?.submittedAt || ""}-${mode}`}
              assignmentId={assignment._id}
              submission={submission}
              submissionState={state}
              onSubmitted={onSubmitted}
              onStale={load}
              onCancel={mode === "edit" ? () => setEditing(false) : undefined}
            />
          ) : submission ? (
            <div className="tw-flex tw-flex-col tw-gap-3">
              {String(submission.textResponse || "").trim() ? (
                <div className="tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3">
                  <LinkedText text={submission.textResponse} />
                </div>
              ) : null}
              {submission.attachments?.length > 0 && <FilePreview attachments={submission.attachments} />}
              <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
                Handed in {formatDue(submission.submittedAt)}
                {submission.attempt > 1 ? ` · attempt ${submission.attempt}` : ""}
                {state?.late ? " · late" : ""}
              </p>
              {state?.canSubmit && mode === "edit" && (
                <div>
                  <AppButton variant="outline" size="sm" onClick={() => setEditing(true)}>Change my work</AppButton>
                </div>
              )}
              {!state?.canSubmit && state?.blockedCode === "graded" && (
                <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{state.blockedReason}</p>
              )}
            </div>
          ) : (
            <Callout tone={state?.state === "missed" ? "danger" : "info"}>
              {state?.blockedReason || "This assignment is not taking submissions."}
            </Callout>
          )}
        </AppCardContent>
      </AppCard>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default StudentAssignmentPage;
